/**
 * Downloading a file the API only hands over to an authenticated caller.
 *
 * A plain link cannot carry the access token or the organization header, so
 * the file is fetched, turned into a blob and saved by a link this code makes
 * and clicks itself. The filename comes from the server, because the server is
 * the one that knows the period the report covers.
 */

const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export interface DownloadAuth {
  token: string | null;
  organizationId: string | null;
  branchId: string | null;
}

export type DownloadOutcome = { saved: true; filename: string } | { saved: false; error: string };

/** Builds a query string, leaving out anything not set. */
export function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  return search.toString();
}

export async function downloadFile(
  path: string,
  params: Record<string, string | number | undefined | null>,
  auth: DownloadAuth,
): Promise<DownloadOutcome> {
  const url = `${API_URL}${path}?${query(params)}`;

  let response: Response;
  try {
    response = await fetch(url, {
      credentials: 'include',
      headers: {
        ...(auth.token ? { authorization: `Bearer ${auth.token}` } : {}),
        ...(auth.organizationId ? { 'x-organization-id': auth.organizationId } : {}),
        ...(auth.branchId ? { 'x-branch-id': auth.branchId } : {}),
      },
    });
  } catch {
    return { saved: false, error: 'Cannot reach the server. Check your connection.' };
  }

  if (!response.ok) {
    // The API refuses in JSON even when the request asked for a file.
    const failure = (await response.json().catch(() => null)) as { message?: string } | null;
    return {
      saved: false,
      error:
        failure?.message ??
        (response.status === 403
          ? 'You do not have permission to export reports.'
          : `The report could not be produced (${response.status}).`),
    };
  }

  const blob = await response.blob();
  const filename = filenameFrom(response) ?? 'report';

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next tick: Safari has not finished reading the blob when
  // click() returns, and a revoked URL saves an empty file.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);

  return { saved: true, filename };
}

function filenameFrom(response: Response): string | null {
  const named = response.headers.get('x-filename');
  if (named) return named;

  const disposition = response.headers.get('content-disposition');
  const quoted = disposition ? /filename="([^"]+)"/.exec(disposition) : null;
  return quoted?.[1] ?? null;
}
